// ===== Registry: parts, objects, goals, skins and worlds =====
// The core's parts (src/kit/) and the worlds' parts (src/worlds/<key>/world.ts) are registered here with the same contract (src/core/types.ts).
// The kit is registered first (src/worlds/index.ts imports it), then the worlds in unlock order.
import type { WorldDef, PartDef, ObjDef, GoalDef, SkinDef, System, KitDef, LevelData } from './types';

/** The world field on the kit's definitions */
export const CORE = 'core';

export const Registry = {
  worlds: {} as Record<string, WorldDef>,   // k -> world definition
  parts: {} as Record<string, PartDef>,     // part type -> definition (incl. world)
  objs: {} as Record<string, ObjDef>,       // object type -> definition (incl. world)
  goals: {} as Record<string, GoalDef>,     // goal type -> definition
  skins: {} as Record<string, SkinDef>,     // the core's skins for fixed blocks (the worlds' are in world.skins)
  systems: [] as System[],                  // system hooks in registration order
  order: [] as string[],                    // the worlds' registration order

  /** The core kit (src/kit/index.ts). Can only be registered once, before the worlds. */
  kit(def: KitDef) {
    if (this.order.length) throw new Error('Registry.kit: register the kit before the worlds');
    this.add(CORE, def.parts, def.objs, def.goals);
    for (const [k, s] of Object.entries(def.skins || {})) {
      if (this.skins[k]) throw new Error('Skin already exists: ' + k);
      this.skins[k] = s;
    }
  },

  world(def: WorldDef): WorldDef {
    if (!def || !def.k) throw new Error('MEK.world: mangler k');
    if (this.worlds[def.k]) throw new Error('MEK.world: ' + def.k + ' findes allerede');
    if (!Object.keys(this.goals).length) throw new Error('MEK.world: import the kit (src/kit) before the worlds');
    def.parts = def.parts || {}; def.objs = def.objs || {}; def.skins = def.skins || {}; def.levels = def.levels || [];
    this.add(def.k, def.parts, def.objs);
    def.levels.forEach((L, i) => { L.wk = def.k; L.wi = i; });
    this.worlds[def.k] = def; this.order.push(def.k);
    return def;
  },

  add(k: string, parts: Record<string, PartDef> = {}, objs: Record<string, ObjDef> = {}, goals: Record<string, GoalDef> = {}) {
    for (const [t, p] of Object.entries(parts)) {
      if (this.parts[t]) throw new Error('Deltype findes allerede: ' + t);
      p.world = k; this.parts[t] = p;
      if (p.system) this.systems.push(p.system);
    }
    for (const [t, o] of Object.entries(objs)) {
      if (this.objs[t]) throw new Error('Objekttype findes allerede: ' + t);
      o.world = k; this.objs[t] = o;
      if (o.system) this.systems.push(o.system);
    }
    for (const [t, g] of Object.entries(goals)) {
      if (this.goals[t]) throw new Error('Goal type already exists: ' + t);
      g.world = k; this.goals[t] = g;
      if (g.system) this.systems.push(g.system);
    }
  },

  /** The kit's part types (a world can reskin these) */
  coreParts(): string[] { return Object.keys(this.parts).filter(t => this.parts[t].world === CORE); },
  /** Part and object types from the kit with an intro animation */
  coreIntros(): string[] { const all: [string, PartDef | ObjDef][] = [...Object.entries(Registry.parts), ...Object.entries(Registry.objs)]; return all.filter(([, d]) => d.world === CORE && d.intro).map(([k]) => k); },
  /** Effect hook for an event from the physics (def.fx[e]) */
  fx(e: string) {
    const all: (PartDef | ObjDef | GoalDef)[] = [...Object.values(Registry.parts), ...Object.values(Registry.objs), ...Object.values(Registry.goals)];
    for (const def of all) { const f = def.fx && def.fx[e]; if (f) return f; }
    return null;
  },
  /** Intro animation for a part or an object */
  intro(k: string | null | undefined) { return (k && ((this.parts[k] && this.parts[k].intro) || (this.objs[k] && this.objs[k].intro))) || null; },
};

// The world files: export default defineWorld({...}). Registers the world (the order is set by src/worlds/index.ts).
export const defineWorld = (def: WorldDef): WorldDef => Registry.world(def);
// Helpers for the kit and the worlds: give the types without registering (registration happens all at once).
export const definePart = <D extends PartDef>(def: D): D => def;
export const defineObj = <St extends object>(def: ObjDef<St>): ObjDef<St> => def;
export const defineGoal = <D extends GoalDef>(def: D): D => def;
/** Level data with typed keys (typos in tools, t, objs, skin, goal and intro are caught by tsc): export const levels = defineLevels([...]) */
export const defineLevels = (levels: LevelData[]): LevelData[] => levels;
